import { Button, Flex, Select, SelectProps, Text } from "@mantine/core";
import { EventHandler, forwardRef, ReactNode, useState } from "react";

import { useModal } from "@/features/modal/ModalStackManager";
import styled from "@emotion/styled";

import { mutateStockPlans } from "@/api/stockPlan/useStockPlanQuery";
import { StockPlanCreateForm } from "@/features/stockPlan/form/CreateForm";
import { handleErrorResponse } from "@/utils/errorMessage";
import { WmsApiStockPlansPostRequest } from "@sizlcorp/sizl-api-document/dist/models";
import { IconX } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import customAlert from "../../alert/Alert";
import {
  StockPlansOption,
  useStockPlansAutoComplete,
  useTransportationCode,
} from "./auto-stockPlanGetQuery";
interface StockProps extends React.ComponentPropsWithoutRef<"div"> {
  // group: string;
  label: string;
  value: string;
  quantity?: string;
}

export interface StockPlanCodesAutoCompleteProps
  extends Partial<SelectProps>,
    Partial<React.RefAttributes<HTMLInputElement>> {
  width?: string;
  value?: string | null;
  onChange?: (transportationCode: string | null, option?: StockProps) => void;
  maxDropdownHeight?: number;
  partnerCode?: string;
  purchaseOrderItemId?: number | null;
}

// 입력값: value (Stock 모델의 code)
// 출력값: onChange (Stock 모델의 code)

export const StockPlanAutoComplete = (params: StockPlanCodesAutoCompleteProps) => {
  const {
    width,
    value: StockPlanCode,
    onChange,
    maxDropdownHeight,
    partnerCode,
    purchaseOrderItemId,
    ...etcParams
  } = params;
  const [focused, setFocused] = useState<boolean>(true);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const { data: options } = useStockPlansAutoComplete(
    focused,
    searchKeyword,
    partnerCode ?? "",
    purchaseOrderItemId ?? 0,
  );
  const { data: initialOptions } = useTransportationCode(!!StockPlanCode, StockPlanCode ?? null);

  let selectedStock = initialOptions?.find((Stock) => Stock.value === StockPlanCode);
  const onChangeHandler = (e: string | null) => {
    const option =
      options?.find((Sites) => Sites.value === e) ??
      initialOptions?.find((Sites) => Sites.value === e);

    onChange?.(e, option);
  };

  const SelectStockPlan = forwardRef<HTMLDivElement, StockProps>(
    ({ label: name, value: code, ...others }, ref) => (
      <div ref={ref} {...others}>
        <Flex direction="row" justify="space-between" align="center">
          <Flex align={"center"}>
            <Text>{code}</Text>
            <Text fz="xs">({name})</Text>
          </Flex>
          {/* <Text fz="xs">{group}</Text> */}
        </Flex>
      </div>
    ),
  );

  return (
    <Select
      styles={
        StockPlanCode
          ? {
              input: {
                width: width,
                fontSize: "0.8em",
                paddingBottom: "0.5rem",
              },
            }
          : { input: { width: width } }
      }
      withinPortal
      onDropdownOpen={() => setFocused(true)}
      onDropdownClose={() => setFocused(false)}
      clearable
      inputContainer={(children: ReactNode) => (
        <div style={{ position: "relative" }}>
          {children}
          {selectedStock?.value && (
            <div
              style={{
                position: "absolute",
                bottom: "-2px",
                fontSize: "0.8em",
                color: "#888",
                left: "5px",
              }}
            >
              {"Code: " + selectedStock?.value}
            </div>
          )}
        </div>
      )}
      value={StockPlanCode}
      itemComponent={SelectStockPlan}
      searchValue={searchKeyword}
      data={[...(options ?? []), ...(initialOptions ?? [])].reduce(
        (unique: StockProps[], option: StockProps) => {
          return unique.some((u) => u.value === option.value) ? unique : [...unique, option];
        },
        [],
      )}
      searchable
      maxDropdownHeight={maxDropdownHeight ?? 150}
      onChange={onChangeHandler}
      onSearchChange={setSearchKeyword}
      rightSection={StockPlanInfo({
        transportation: selectedStock as StockPlansOption,
        onChange: onChangeHandler,
      })}
      filter={(value, item) =>
        item?.label?.toLowerCase().includes(value.toLowerCase().trim()) ||
        item?.value?.toLowerCase().includes(value.toLowerCase().trim())
      }
      nothingFound={AddNewStock({
        stockPlanName: searchKeyword,
        onChange: onChangeHandler,
        partnerCode: partnerCode,
      })}
      {...etcParams}
    />
  );
};

const StockPlanInfo = (params: {
  transportation?: StockPlansOption;
  onChange: (transportationCode: string | null) => void;
}) => {
  const { transportation, onChange } = params;

  const clearHandler: React.MouseEventHandler<SVGSVGElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange("");
  };

  return transportation?.value ? (
    <StockPlanInfoLabel>
      {/* ({Stock.value}) */}
      <IconX size="1rem" onClick={clearHandler} />
    </StockPlanInfoLabel>
  ) : null;
};

const AddNewStock = (params: {
  stockPlanName: string;
  onChange: (transportationCode: string) => void;
  partnerCode?: string;
}) => {
  const { stockPlanName, onChange, partnerCode } = params;
  const { openModal, closeModal } = useModal();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { mutate } = useMutation(
    (params: WmsApiStockPlansPostRequest) =>
      mutateStockPlans.create(params).mutationFn(params as WmsApiStockPlansPostRequest | any),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(["stockPlans"]);
        customAlert(
          t("message.createSuccess", { item: t("stockPlan.stockPlan") }),
          t("common.createSuccess"),
          "green",
        );
        closeModal(res.data);
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("stockPlan.stockPlan") }),
          t("common.createFail", { item: t("stockPlan.stockPlan") }),
          "red",
        );
      },
    },
  );

  const onCloseHandler: EventHandler<any> = (values) => {
    mutate({
      stockPlansGetRequest: {
        partnerCode: values.partnerCode!,
        direction: "INBOUND",
        code: values.code,
        scheduledAt: values.scheduledAt!,
        itemCode: values.itemCode!,
        lotId: values.lotId!,
        quantity: values.quantity!,
        userCode: values.userCode!,
        sourceLocationCode: values.sourceLocationCode!,
        targetLocationCode: values.targetLocationCode!,
        transportationId: values.transportationId!,
      },
    });
  };
  const addNewModalHandler: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    openModal(
      <StockPlanCreateForm
        code={stockPlanName}
        partnerCode={partnerCode}
        autoCompleteOnSubmit={onCloseHandler}
      />,
      null,
      t("common.newCreate", { item: t("stockPlan.stockPlan") }),
      true,
    ).then((result) => {
      onChange(result?.code);
    });
  };

  return (
    <Button variant="subtle" onClick={addNewModalHandler}>
      {t("common.newCreate", { item: `${t("stockPlan.stockPlan")} ${stockPlanName}` })}
    </Button>
  );
};
const StockPlanInfoLabel = styled.div`
  font-size: 12px;
  color: #666666;
  // padding-right: 8px;
  padding-left: 0.4rem;
  display: flex;
  align-items: center;
  position: absolute;
`;
